/* eslint-disable max-lines */
import { useState, useEffect, useMemo } from 'react';
import * as TrackingService from 'common/services/tracking.service';
import {
  PM_PROVIDERS,
  PROVIDERS_MAP,
  SESSION_STORAGE_KEYS,
  TRACKING,
  DASHBOARD_INTERNAL_TYPES,
  PM_METRICS,
  DASHBOARD_NAMES
} from 'common/constants/constants';
import {
  getDashboardsExcludeTeamGoals,
  createDashboard,
  updateDashboard,
  deleteDashboard
} from 'common/services/metrics.service';
import {
  PRODUCTIVITY_REPORTS,
  PM_REPORTS,
  EMPTY_DEFAULT_REPORT
} from './reportsConfiguration';
import BrowserStorageAccessor from 'common/utils/BrowserStorageAccessor';
import _ from 'lodash';
import { SettingValues } from 'common/services/settings.service.js';
import {
  pluralize,
  getTeamSelectedProjects,
  isTeamByLicense
} from 'common/utils/utils';
import { NewCustomReport } from './views/CustomReport';
import Jira from './views/Jira';
import { EMPTY_DUMMY_ORG_LEVEL_TEAM_GOALS_DASHBOARD } from './views/CustomReport/consts';
import { isEqualAllTeams } from './views/CustomReport/Filters/TeamsDropdown';
import AppRouter from 'common/AppRouter';
import { useDispatch, useSelector } from 'react-redux';
import { myAccountSelector } from 'state/selectors/account';
import {
  gitIntegrationsSelector,
  pmIntegrationSelector
} from 'state/selectors/sensors-authorizations';
import { licenseSelector } from 'state/selectors/license';
import { updatePinnedBoards } from 'state/callers';
import { selectedTeamSelector } from 'state/selectors/ui-state';
import { repositoriesSelector } from 'state/selectors/repositories';
import { useHistory, useRouteMatch } from 'react-router';
import { teamsSelector } from 'state/selectors/teams';
import {
  currentIterationSelector,
  iterationsSelector
} from 'state/selectors/iterations-picker';
import { allProjectsSelector } from 'state/selectors/projects';
import useSettingsService from 'components/hooks/useSettingsService';
import { FEATURE_FLAGS_NAMES } from '@linearb/linjs-common';
import { widgetFormatterByProvider } from './views/CustomReport/Widgets/utils/widgetFormatterByProvider';
import GitTypes from 'common/constants/GitTypes';

const REPORT_TYPES = {
  productivity: 'productivity',
  metrics: 'metrics'
};

const DEFAULT_REPORT_NAME = 'Dashboard 1';

// eslint-disable-next-line max-lines-per-function
const useDashboards = ({
  filters,
  disableTeamPicker,
  setDisableTeamPicker
}) => {
  const match = useRouteMatch();
  const dispatch = useDispatch();
  const history = useHistory();
  const currentAccount = useSelector(myAccountSelector);
  const pmSensor = useSelector(pmIntegrationSelector);
  const license = useSelector(licenseSelector);
  const selectedTeam = useSelector(selectedTeamSelector);
  const repositories = useSelector(repositoriesSelector);
  const currentTeams = useSelector(teamsSelector);
  const currentIteration = useSelector(currentIterationSelector);
  const projects = useSelector(allProjectsSelector);
  const iterations = useSelector(iterationsSelector);
  const { getSettingOrDefinitionValue } = useSettingsService();

  const [customDashboardsFiltered, setCustomDashboardsFiltered] = useState([]);
  const [reports, setReports] = useState([]);

  const [customDashboards, setCustomDashboards] = useState([]);
  const [sideBarDashboards, setSideBarDashboards] = useState([]);
  const [newCustomDashBoard, setNewCustomDashBoard] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [constantReports, setConstantReports] = useState([]);
  const [selectedReport, setSelectedReport] = useState(
    JSON.parse(
      BrowserStorageAccessor().session.get(
        SESSION_STORAGE_KEYS.SELECTED_DASHBOARDS
      )
    ) || null
  );

  const giIntegrations = useSelector(gitIntegrationsSelector) || [];

  // Check if at least one git integration supports AI Code Review dashboard
  const hasAICodeReviewSupportedGitIntegration = useMemo(() => {
    return giIntegrations.some(integration => {
      const { sensor_name, is_self_hosted } = integration;

      return (
        sensor_name === GitTypes.GITHUB ||
        sensor_name === GitTypes.GITLAB ||
        (sensor_name === GitTypes.BITBUCKET && !is_self_hosted)
      );
    });
  }, [giIntegrations]);

  const customPmMetricsForAzureBoardsFF = getSettingOrDefinitionValue({
    name: FEATURE_FLAGS_NAMES.customPmMetricsForAzureBoardsEnabled
  });

  const isReportedMetricsEnabled = getSettingOrDefinitionValue({
    name: FEATURE_FLAGS_NAMES.reportedMetricsEnabled
  });

  const customPmMetricsForAzureBoardsEnabled = customPmMetricsForAzureBoardsFF;

  const CUSTOM_PM_METRICS_SUPPORTED_PROVIDERS = [
    PM_PROVIDERS.JIRA,
    customPmMetricsForAzureBoardsEnabled && PM_PROVIDERS.AZURE_BOARDS
  ].filter(Boolean);

  const pinnedDashboards = _.get(
    currentAccount,
    'settings.pinned_dashboards',
    []
  );
  const [canCreateMoreCustomDashboards, setCanCreateMoreCustomDashboards] =
    useState(false);

  const getSideBarDashboardsWithoutGoals = ({ internal_type, team_id }) =>
    internal_type !== DASHBOARD_INTERNAL_TYPES.TEAM_GOALS;

  const getTeamGoalDashboard = ({ internal_type, team_id }) =>
    filters.firstGloballySelectedTeamId
      ? internal_type === DASHBOARD_INTERNAL_TYPES.TEAM_GOALS &&
        filters.firstGloballySelectedTeamId === team_id
      : internal_type === DASHBOARD_INTERNAL_TYPES.TEAM_GOALS && !team_id;

  const hasPMSensor =
    pmSensor && Object.values(PM_PROVIDERS).includes(pmSensor.sensor_id);

  const selectedProjectSetting =
    getSettingOrDefinitionValue({
      name: SettingValues.PM_IMPORT_STRATEGY
    }) || {};

  const selectedTeamProject = // (only one specific team selected) ? team[0].project_id : null
    !isEqualAllTeams(filters.globallySelectedTeams) &&
    _.size(filters.globallySelectedTeams) === 1
      ? projects.find(
          ({ id }) =>
            id === _.get(filters.globallySelectedTeams, '[0].project_id')
        )
      : null;

  const selectedTeamProjects =
    selectedTeamProject && pmSensor
      ? getTeamSelectedProjects(
          selectedTeamProject,
          selectedProjectSetting,
          pmSensor.sensor_name
        )
      : [];

  const maxDashboards = _.get(license, 'maxDashboards', 5);

  const resetNewReport = () => setNewCustomDashBoard(null);
  const selectReportAndRedirect = (report, route) => {
    if (report.internal_type !== DASHBOARD_INTERNAL_TYPES.TEAM_GOALS) {
      BrowserStorageAccessor().session.set(
        SESSION_STORAGE_KEYS.SELECTED_DASHBOARDS,
        JSON.stringify(report)
      );
    }
    setSelectedReport(report);
    history.push({
      pathname: `${match.path}/${route}`,
      search: history.location.search
    });
  };

  const loadDashboards = async () => {
    setLoaded(false);
    resetNewReport();
    const dashboardsResult = await getDashboardsExcludeTeamGoals();

    const boardsWithFormattedWidgets = widgetFormatterByProvider(
      dashboardsResult,
      pmSensor?.sensor_id
    );
    const isCanCreateMoreCustomDashboards = _.get(
      boardsWithFormattedWidgets,
      'isCanCreateMoreCustomDashboards',
      false
    );
    setCanCreateMoreCustomDashboards(isCanCreateMoreCustomDashboards);
    const dashboards = [
      EMPTY_DUMMY_ORG_LEVEL_TEAM_GOALS_DASHBOARD,
      ..._.get(boardsWithFormattedWidgets, 'items', [])
    ].filter(Boolean);

    const metricDefaultDashboards = dashboards.filter(
      dashboard => dashboard.is_default
    );

    const teamGoalsDashboards = _.uniqBy(
      dashboards.filter(
        dashboard =>
          dashboard.internal_type === DASHBOARD_INTERNAL_TYPES.TEAM_GOALS
      ),
      ({ name }) => name
    );

    const editableDashboards = dashboards
      .filter(dashboard => dashboard.definition.is_editable === true)
      .filter(
        ({ internal_type }) =>
          internal_type !== DASHBOARD_INTERNAL_TYPES.CUSTOM_PM_METRICS ||
          CUSTOM_PM_METRICS_SUPPORTED_PROVIDERS.includes(pmSensor?.sensor_id)
      );

    const formattedDashboards =
      formatCustomMetricDashboardName(editableDashboards);

    const customDashboards = [...teamGoalsDashboards, ...formattedDashboards];

    setConstantReports([
      ...metricDefaultDashboards,
      ...PRODUCTIVITY_REPORTS,
      ...PM_REPORTS
    ]);

    setCustomDashboards([...customDashboards]);
    setLoaded(true);
    return [...metricDefaultDashboards, ...customDashboards];
  };

  const getAdditionalText = type => {
    if (!type) return false;
    switch (type) {
      case PM_METRICS:
        if (!hasPMSensor || !pmSensor || !pmSensor.sensor_name) return false;
        const providerConfig =
          PROVIDERS_MAP[pmSensor.sensor_name.toLocaleLowerCase()];
        if (isEqualAllTeams(filters.globallySelectedTeams))
          return providerConfig.metrics_all_teams;
        return selectedTeamProjects.length
          ? `${providerConfig.name} ${pluralize(
              selectedTeamProjects.length,
              `${providerConfig.entity_name}`,
              undefined,
              false
            )}: ${selectedTeamProjects
              .filter(Boolean)
              .map(({ name }) => name)
              .filter(Boolean)
              .join(', ')}`
          : null;
      default:
        return false;
    }
  };

  const onSaveDashboardChanges = async dashboard => {
    let savedDashboard;
    const isNew = dashboard.id === EMPTY_DEFAULT_REPORT.id;
    if (isNew) {
      savedDashboard = await createDashboard(_.omit(dashboard, ['id']));
    } else {
      TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
        page: TRACKING.PAGES.METRICS,
        page_tab: TRACKING.PAGE_TABS.DASHBOARD_EDIT,
        click_type: TRACKING.CLICK.DASHBOARD_SAVE,
        access: dashboard.is_public ? 'Public' : 'Private'
      });
      savedDashboard = await updateDashboard(dashboard);
    }
    if (
      (isNew || (!savedDashboard.is_public && selectedReport.is_public)) &&
      !pinnedDashboards.includes(savedDashboard.id)
    ) {
      await updatePinnedBoards([...pinnedDashboards, savedDashboard.id])(
        dispatch
      );
    }
    await loadDashboards();
    selectReportAndRedirect(savedDashboard, savedDashboard.id);
  };

  const CustomReportComponent = NewCustomReport;

  const onDeleteDashboard = async id => {
    TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
      page: TRACKING.PAGES.METRICS,
      click_type: TRACKING.CLICK.DELETE_DASHBOARD
    });
    await deleteDashboard(id);
    await loadDashboards();
    selectReportAndRedirect(reports[0], reports[0].route);
  };

  const definitionToReport = definition => ({
    component: () =>
      definition.type === 'pm' ? (
        <Jira
          name={definition.name}
          pmSensor={pmSensor}
          currentTeams={currentTeams}
          filters={filters}
          onSelectReport={onSelectReport}
        />
      ) : (
        <CustomReportComponent
          report={definition}
          filters={filters}
          customDashboards={customDashboards}
          selectReportAndRedirect={selectReportAndRedirect}
          articleId={definition.articleId}
          additionalText={getAdditionalText(definition.type)}
          selectedTeam={selectedTeam}
          repositories={repositories}
          currentTeams={currentTeams}
          currentIteration={currentIteration}
          iterations={iterations}
          saveDashboard={onSaveDashboardChanges}
          deleteDashboard={onDeleteDashboard}
          isDashboardNameValid={dashboardName =>
            !customDashboards
              .filter(({ account_id }) => account_id === currentAccount.id)
              .map(({ name }) => name)
              .includes(dashboardName)
          }
          pmSensor={pmSensor}
          disableTeamPicker={disableTeamPicker}
          setDisableTeamPicker={setDisableTeamPicker}
        />
      ),
    pmRequired: definition.type === 'pm',
    route: definition.id,
    ...definition
  });

  const newFormattedBoard = newCustomDashBoard
    ? definitionToReport(newCustomDashBoard)
    : null;

  const initCustomDashboardsFiltered = () => {
    const customDashboardsFiltered = customDashboards
      .map(definitionToReport)
      .filter(({ pmRequired }) => !pmRequired || hasPMSensor)
      .filter(({ type }) => type !== REPORT_TYPES.productivity);
    setCustomDashboardsFiltered(customDashboardsFiltered);
  };

  const initReports = () => {
    const reports = constantReports
      .map(definitionToReport)
      .filter(({ pmRequired }) => !pmRequired || hasPMSensor)
      .filter(({ type }) => type !== REPORT_TYPES.productivity)
      .filter(
        ({ supportProviders }) =>
          !Array.isArray(supportProviders) ||
          supportProviders.includes(pmSensor?.sensor_id)
      )
      .filter(
        ({ id }) =>
          id !== 'AI Code Review' || hasAICodeReviewSupportedGitIntegration
      );
    setReports(reports);
  };

  const onSelectReport = useMemo(
    () =>
      ({ id: selectedReportId }) => {
        const report = [...reports, ...customDashboardsFiltered].find(
          ({ id }) => id === selectedReportId
        );
        if (report) {
          selectReportAndRedirect(report, report.route);
        }
      },
    [reports, customDashboardsFiltered]
  );

  const generateNewReport = useMemo(
    () => () => {
      const emptyReport = {
        ...EMPTY_DEFAULT_REPORT,
        ...(isTeamByLicense(license) ? { is_public: true } : {})
      };
      emptyReport.name = DEFAULT_REPORT_NAME;

      const latestNumber = customDashboards
        .map(({ name }) => name)
        .filter(i => /^(Dashboard \d+$)/.test(i))
        .map(name => +name.split(' ').pop())
        .sort((name1, name2) => name1 - name2)
        .pop();

      if (!latestNumber) {
        return emptyReport;
      }

      if (!isNaN(latestNumber)) {
        emptyReport.name = `Dashboard ${latestNumber + 1}`;
        return emptyReport;
      }
      return emptyReport;
    },
    [customDashboards]
  );

  const getAllDashboards = async () => {
    const dashboards = [
      ...(await loadDashboards()),
      ...PM_REPORTS,
      ...PRODUCTIVITY_REPORTS
    ];

    const pathReport = dashboards.find(({ id }) =>
      history.location.pathname.includes(id)
    );
    const selectedDashboard =
      pathReport ||
      dashboards.find(({ id }) => selectedReport && id === selectedReport.id);

    if (selectedDashboard) {
      selectReportAndRedirect(selectedDashboard, selectedDashboard.id);
    } else if (dashboards.length > 0) {
      const filteredDashboards = dashboards.filter(
        item => hasPMSensor || _.get(item, 'type') !== PM_METRICS
      );
      selectReportAndRedirect(filteredDashboards[0], filteredDashboards[0].id);
    }
  };

  const formatCustomMetricDashboardName = dashboards => {
    return dashboards.map(dashboard => {
      return dashboard.internal_type ===
        DASHBOARD_INTERNAL_TYPES.CUSTOM_PM_METRICS &&
        dashboard.auto_created &&
        isReportedMetricsEnabled
        ? {
            ...dashboard,
            name: DASHBOARD_NAMES.CUSTOM_METRICS
          }
        : dashboard;
    });
  };

  useEffect(() => {
    const filteredDashboards = customDashboards.filter(
      getSideBarDashboardsWithoutGoals
    );

    setSideBarDashboards(filteredDashboards);
  }, [constantReports, customDashboards, filters.firstGloballySelectedTeamId]);

  useEffect(() => {
    initCustomDashboardsFiltered();
    initReports();
  }, [
    constantReports,
    customDashboards,
    hasPMSensor,
    filters.globallySelectedTeams,
    filters.selectedLabels,
    filters.selectedTimeRanges,
    filters.selectedContributor,
    filters.selectedGranularity,
    filters.selectedRepo,
    filters.selectedService,
    filters.filterType
  ]);

  useEffect(() => {
    getAllDashboards();
  }, []);

  useEffect(() => {
    const isTeamGoalsDashboardCurrentlySelected = customDashboards.find(
      ({ id, internal_type }) =>
        history.location.pathname.includes(id) &&
        internal_type === DASHBOARD_INTERNAL_TYPES.TEAM_GOALS
    );
    const teamGoalsDashboard = customDashboards.find(getTeamGoalDashboard);
    if (isTeamGoalsDashboardCurrentlySelected) {
      selectReportAndRedirect(teamGoalsDashboard, teamGoalsDashboard.id);
    }
  }, [filters.firstGloballySelectedTeamId]);

  useEffect(() => {
    if (
      selectedReport &&
      !history.location.pathname.includes(selectedReport.id) &&
      history.location.pathname === _.get(AppRouter, 'PERFORMANCE.path', '')
    ) {
      history.push({
        pathname: `${match.path}/${selectedReport.id}`,
        search: history.location.search
      });
    }
  }, [selectedReport, history.location.pathname]);

  return {
    sideBarDashboards,
    loaded,
    newCustomDashBoard,
    selectedReport,
    onSelectReport,
    setNewCustomDashBoard,
    generateNewReport,
    setSelectedReport,
    maxDashboards,
    newFormattedBoard,
    reports,
    customDashboardsFiltered,
    pinnedDashboards,
    canCreateMoreCustomDashboards
  };
};

export default useDashboards;
